import type { Insumo } from '@pgcustom/shared-types';
import { ApiError } from '../../core/api-client.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { EstoqueApi } from './estoque.api.js';

type Modo = 'criar' | 'editar';

/** `<erp-insumo-form-modal>` — criação/edição de insumo (nome, unidade, ponto de reposição). Mesmo padrão de `erp-transacao-form-modal`. */
export class ErpInsumoFormModal extends HTMLElement {
  private readonly root: ShadowRoot;
  private _estoqueApi: EstoqueApi | null = null;
  private _aoSalvar: ((insumo: Insumo) => void) | null = null;

  private aberto = false;
  private modo: Modo = 'criar';
  private insumoAtual: Insumo | null = null;
  private enviando = false;
  private mensagemErro = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set estoqueApi(valor: EstoqueApi) {
    this._estoqueApi = valor;
  }

  set aoSalvar(valor: (insumo: Insumo) => void) {
    this._aoSalvar = valor;
  }

  connectedCallback(): void {
    this.render();
  }

  abrirParaCriar(): void {
    this.modo = 'criar';
    this.insumoAtual = null;
    this.mensagemErro = '';
    this.aberto = true;
    this.render();
  }

  abrirParaEditar(insumo: Insumo): void {
    this.modo = 'editar';
    this.insumoAtual = insumo;
    this.mensagemErro = '';
    this.aberto = true;
    this.render();
  }

  private fechar(): void {
    if (this.enviando) return;
    this.aberto = false;
    this.render();
  }

  private async aoSubmeter(evento: Event): Promise<void> {
    evento.preventDefault();
    if (!this._estoqueApi || this.enviando) return;

    const nome = this.valorCampo('#nome').trim();
    const unidade = this.valorCampo('#unidade').trim();
    const pontoReposicaoBruto = this.valorCampo('#ponto-reposicao').trim();
    const pontoReposicao = pontoReposicaoBruto ? Number(pontoReposicaoBruto) : 0;

    if (!nome) {
      this.mensagemErro = 'nome é obrigatório';
      this.render();
      return;
    }
    if (!unidade) {
      this.mensagemErro = 'unidade é obrigatória';
      this.render();
      return;
    }
    if (!Number.isInteger(pontoReposicao) || pontoReposicao < 0) {
      this.mensagemErro = 'ponto de reposição deve ser um inteiro não-negativo';
      this.render();
      return;
    }

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      let insumo: Insumo;
      if (this.modo === 'criar') {
        insumo = await this._estoqueApi.criarInsumo({ nome, unidade, pontoReposicao });
      } else {
        if (!this.insumoAtual) throw new Error('insumo ausente para edição');
        insumo = await this._estoqueApi.atualizarInsumo(this.insumoAtual.id, { nome, unidade, pontoReposicao });
      }
      this.aberto = false;
      this._aoSalvar?.(insumo);
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível salvar. tente novamente.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private valorCampo(seletor: string): string {
    return this.root.querySelector<HTMLInputElement>(seletor)?.value ?? '';
  }

  private render(): void {
    const insumo = this.insumoAtual;
    const titulo = this.modo === 'criar' ? 'Novo insumo' : 'Editar insumo';

    this.root.innerHTML = `
      <style>
        :host { display: block; }
        form { display: flex; flex-direction: column; gap: var(--pg-space-4, 16px); font-family: var(--pg-font-familia-base, system-ui); }
        ${FORM_FIELD_CSS}
        .rodape { display: flex; justify-content: flex-end; gap: var(--pg-space-3, 12px); }
      </style>
      <pg-modal id="modal" ${this.aberto ? 'open' : ''}>
        <span slot="header">${escaparHtml(titulo)}</span>
        <form novalidate>
          <label>Nome <input id="nome" type="text" maxlength="255" value="${escaparHtml(insumo?.nome ?? '')}" /></label>
          <label>Unidade (ex: L, un, kg) <input id="unidade" type="text" maxlength="20" value="${escaparHtml(insumo?.unidade ?? '')}" /></label>
          <label>Ponto de reposição <input id="ponto-reposicao" type="number" min="0" value="${insumo ? String(insumo.pontoReposicao) : '0'}" /></label>
          <span class="erro">${escaparHtml(this.mensagemErro)}</span>
        </form>
        <div slot="footer" class="rodape">
          <pg-button id="botao-cancelar" variant="ghost">Cancelar</pg-button>
          <pg-button id="botao-salvar" variant="primary" ${this.enviando ? 'loading' : ''}>Salvar</pg-button>
        </div>
      </pg-modal>
    `;

    const form = this.root.querySelector('form');
    form?.addEventListener('submit', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-salvar')?.addEventListener('pg-click', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-cancelar')?.addEventListener('pg-click', () => this.fechar());
    this.root.querySelector('#modal')?.addEventListener('pg-close', () => this.fechar());
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-insumo-form-modal', ErpInsumoFormModal);

declare global {
  interface HTMLElementTagNameMap {
    'erp-insumo-form-modal': ErpInsumoFormModal;
  }
}
